import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, createElementVNode as _createElementVNode, resolveDirective as _resolveDirective, openBlock as _openBlock, createElementBlock as _createElementBlock, withDirectives as _withDirectives, mergeProps as _mergeProps, vShow as _vShow, Fragment as _Fragment } from "vue"

const _hoisted_1 = { class: "block w-full" }
const _hoisted_2 = { class: "flex mb-4" }
const _hoisted_3 = ["title"]
const _hoisted_4 = /*#__PURE__*/_createElementVNode("span", { class: "icon-neu" }, null, -1)
const _hoisted_5 = [
  _hoisted_4
]
const _hoisted_6 = ["disabled", "title"]
const _hoisted_7 = /*#__PURE__*/_createElementVNode("span", { class: "icon-loeschen" }, null, -1)
const _hoisted_8 = [
  _hoisted_7
]
const _hoisted_9 = { class: "ml-auto" }
const _hoisted_10 = { class: "text-center" }

import { onBeforeUnmount, onMounted, reactive, ref } from "vue";
    import { BryntumGrid } from "@bryntum/grid-vue-3";
    import { Menu, Store } from "@bryntum/grid";
    import GridInputFilter from "@/components/GridInputFilter.vue";
    import { useGlobalStore } from "@/stores/GlobalStore";
    import { ErrorLocationType, PopupType, TableType } from '@/utilities/enums/Enums';
    import { $t } from "@/utilities/i18n/config";
    import dialog from "@/dialog";
    import ErrorHandler from "@/utilities/helpers/ErrorHandler";
    import Error from "@/components/Error.vue";
    import { useI18n } from "vue-i18n";
    import { checkboxColumnConfig, defaultSpaceAroundTableOnPage } from "@/AppConfig";
    import { ActivityInfo } from "@/models/activity/ActivityInfo";
    import LoadingSpinner from "@/components/LoadingSpinner.vue";
    import { ActivityService } from "@/utilities/services/entities/ActivityService";
    import { PermissionService } from "@/utilities/services/PermissionService";
    import NullOrUndefinedHelper from "@/utilities/helpers/NullOrUndefinedHelper";

    
export default /*#__PURE__*/_defineComponent({
  __name: 'ActivityTab',
  setup(__props) {

    const gridStore = new Store();
    const globalStore = useGlobalStore();

    let selectedActivities = ref([]);

    const { t } = useI18n();
    const activityService = new ActivityService();
    const permissionService = new PermissionService();


    const deleteSelection = async () => {
        //drop call without selection
        if (!selectedActivities.value?.some(() => true)) return;

        const answer = await dialog
            .okText(t("global.delete"))
            .cancelText(t("global.cancel"))
            .confirm(t("activity.confirmDeletionMulti", { count: selectedActivities.value.length }));

        if (answer) {
            let someEntriesFailed = false;
            for (const a of selectedActivities.value) {
                const resp = await activityService.deleteActivity(a.id);

                if (resp === null || resp === undefined || resp?.statusCode !== 0) {
                    ErrorHandler.handleError(resp, ErrorLocationType.table);
                    someEntriesFailed = true;
                    continue;
                }

                gridStore.applyChangeset({ removed: [{ id: a.id }] });
            }

            if (someEntriesFailed) return;

            globalStore.showSuccess(t("global.deleted"));
        }
    };

    const currentWindowHeight = ref("");
    const recalcGridHeight = () => {
        currentWindowHeight.value = `${window.innerHeight - defaultSpaceAroundTableOnPage - 75}px`;
    };

    const grid = ref(null);
    const useGridConfig = {
        relayStoreEvents: true,
        headerMenuFeature: false,
        columns: {
            data: [
                { id: 'name', field: 'name', text: t('activity.grid.name'), flex: 1, },
                {
                    id: 'categoryId',
                    field: 'categoryId',
                    text: t('activity.grid.category'),
                    flex: 1,
                    type: "template",
                    template: ({ record }: any) => {
                        const category = record.data.categoryTitle;
                        const iconSpan = `<span class="icon-${record.data.iconName}"/>`;
                        return NullOrUndefinedHelper(category) ? "" : `${category} (${iconSpan})`;
                    }
                },
                {
                    id: 'menu',
                    text: '',
                    width: 36,
                    type: 'widget',
                    align: "center",
                    hideable: false,
                    resizable: false,
                    sortable: false,
                    filterable: false,
                    widgets: [{
                        type: 'button',
                        width: 36,
                        icon: 'icon-kontext_horizontal',
                        onAction: ({ source: btn }) => {
                            const menu = menuFactory(btn.cellInfo.record.data);
                            menu.alignTo({ target: btn })
                            menu.show();
                        }
                    }]
                },
            ],
            listeners: {
                async add(event: any) {
                    if (event.records.length === 1 && event.isMove[event.records[0].id]) {
                        await globalStore.saveUserColumnOrder(TableType[TableType.activity], event.records[0].id, event.index.toString());
                    }
                },
            }
        },
        store: gridStore,
        enableTextSelection: true,
        selectionMode: {
            row: true,
            checkbox: checkboxColumnConfig,
            showCheckAll: true,
            deselectFilteredOutRecords: true
        },
        listeners: {
            selectionChange(event) {
                if (event.selection.length > 0 && event.selection[0]?.data === undefined) return;
                selectedActivities.value = selectedActivities.value.concat(event.selected.map(ar => ar.data))
                selectedActivities.value = selectedActivities.value.filter(a => !event.deselected.map(ar => ar.data).some(ar => a?.id === ar?.id));
            },
            datachange() {
                globalStore.currentItems = gridStore.count;
                globalStore.allItems = gridStore.originalCount;
            },
        },
        height: currentWindowHeight,
    }

    const menuFactory = (record: object) => {
        const menu = new Menu({
            anchor: true,
            autoShow: true,
            items: [
                {
                    text: t("global.edit"),
                    cls: 'color',
                    icon: 'icon-editieren',
                    weight: 100,
                    onItem: ({ menu }) => {
                        globalStore.openPopup({
                            data: JSON.parse(JSON.stringify(menu.record)),
                            action: addOrUpdateActivity,
                            resetButton: false,
                            title: $t("popups.activity.edit"),
                            type: PopupType.activityEditPopup
                        });
                    }
                },
                {
                    text: t("global.delete"),
                    disabled: !permissionService.userHasPermission("ActivitySets.Show"),
                    hidden: false,
                    cls: 'color',
                    icon: 'icon-loeschen',
                    weight: 140,
                    onItem: async ({ menu }) => {
                        const answer = await dialog
                            .okText(t("global.delete"))
                            .cancelText(t("global.cancel"))
                            .confirm(t("activity.confirmDeletionSingle", { name: menu.record.name }));

                        if (answer) {
                            const resp = await activityService.deleteActivity(menu.record.id);

                            if (resp === null || resp === undefined || resp?.statusCode !== 0) {
                                ErrorHandler.handleError(resp, ErrorLocationType.table);
                                return;
                            }

                            gridStore.applyChangeset({ removed: [{ id: menu.record.id }] });

                            globalStore.showSuccess(t('global.deleted'));
                        }
                    }
                }
            ],
        });

        menu.setRecord(record);

        return menu;
    };


    const gridConfig = reactive(useGridConfig);

    const newActivity = () => {
        globalStore.openPopup({
            data: new ActivityInfo(),
            action: addOrUpdateActivity,
            resetButton: false,
            title: $t("popups.activity.new"),
            type: PopupType.activityEditPopup
        });
    }

    const addOrUpdateActivity = async () => {
        const activity = globalStore.modal.contentObject as ActivityInfo;

        const successOrId = await activityService.addOrUpdateActivity(activity, ErrorLocationType.popup);
        if (successOrId === false || successOrId === -1) return;

        if (activity.id < 0) activity.id = successOrId as number;
        // fix selection when it was an update -> replace old value in selected-array
        else if (selectedActivities.value.some(el => el.id === activity.id)) {
            selectedActivities.value = selectedActivities.value.filter(el => el.id !== activity.id);
            selectedActivities.value.push(activity);
        }

        gridStore.add(activity);
        gridStore.sort(gridStore.sorters);

        globalStore.closeModal();
        globalStore.showSuccess(t('global.saved'));
        await setGridData();
    }

    const setGridData = async () => {
        let activities = await activityService.getActivities("display", ErrorLocationType.table);
        if (activities === null) return;

        // add searchable categoryString just for this view
        activities = activities?.map(a => {
            //@ts-expect-error
            a.categorySearchString = a.categoryTitle;
            return a;
        })

        gridStore.setStoreData(activities);

        globalStore.reorderColumns(TableType[TableType.activity]);
    }

    onMounted(async () => {
        // calculate grid height and sub to window resize event
        recalcGridHeight();
        window.addEventListener('resize', recalcGridHeight);

        globalStore.setGridStore(ref(gridStore));
        globalStore.setGridColumnStore(ref(grid.value?.instance.value?.columns));

        globalStore.gridDataIsLoading = true;

        await setGridData();

        globalStore.gridDataIsLoading = false;

    });

    onBeforeUnmount(() => {
        window.removeEventListener('resize', recalcGridHeight);

        globalStore.setGridStore(null);
        globalStore.setGridColumnStore(null);

        globalStore.currentItems = 0;
        globalStore.allItems = 0;
    });

return (_ctx: any,_cache: any) => {
  const _directive_permission = _resolveDirective("permission")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _withDirectives(_createElementVNode("div", null, [
      _createVNode(Error, {
        errorLoc: _unref(ErrorLocationType).table
      }, null, 8, ["errorLoc"]),
      _createElementVNode("div", _hoisted_1, [
        _createElementVNode("div", _hoisted_2, [
          _createElementVNode("button", {
            class: "primary mr-2",
            onClick: newActivity,
            title: _unref(t)('popups.activity.new')
          }, _hoisted_5, 8, _hoisted_3),
          _withDirectives((_openBlock(), _createElementBlock("button", {
            class: "primary mr-2",
            disabled: !_unref(selectedActivities).some(()=>true),
            onClick: deleteSelection,
            title: _unref(t)('global.delete')
          }, _hoisted_8, 8, _hoisted_6)), [
            [_directive_permission, 'ActivitySets.Show']
          ]),
          _createElementVNode("div", _hoisted_9, [
            _createVNode(GridInputFilter, {
              gridStore: _unref(gridStore),
              hideFilterButton: true
            }, null, 8, ["gridStore"])
          ])
        ]),
        _createVNode(_unref(BryntumGrid), _mergeProps({
          ref_key: "grid",
          ref: grid
        }, gridConfig, { readOnly: true }), null, 16)
      ])
    ], 512), [
      [_vShow, !_unref(globalStore).gridDataIsLoading]
    ]),
    _withDirectives(_createElementVNode("div", _hoisted_10, [
      _createVNode(LoadingSpinner)
    ], 512), [
      [_vShow, _unref(globalStore).gridDataIsLoading]
    ])
  ], 64))
}
}

})